import { useState, useRef } from 'react';
import Spinner from '../common/Spinner';
import { api } from '../../services/apiClient';
import { todayStr } from '../../utils/formatters';

export default function UploadModal({ type, onDone, onClose, toast }) {
  const [files, setFiles] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  function addFiles(newFiles) {
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...Array.from(newFiles).filter(f => !existing.has(f.name))];
    });
  }

  async function submit() {
    if (!files.length) return;
    setUploading(true);
    setProgress(`Uploading ${files.length} receipt(s)...`);
    try {
      const result = await api.uploadReceipts(files, type, date);
      setProgress(`✓ Parsed ${result.parsedCount} receipts — Invoice ${result.invoice?.invoiceNo} saved!`);
      toast(`Invoice ${result.invoice?.invoiceNo} created and saved to Drive`, 'success');
      setTimeout(() => { onDone(result.invoice); onClose(); }, 1400);
    } catch (e) {
      setProgress('');
      toast('Upload failed: ' + e.message, 'error');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">📷 Upload {type === 'sale' ? 'Sale' : 'Purchase'} Receipts</span>
          <button className="btn btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div
            className={`upload-zone ${drag ? 'drag-over' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
          >
            <div className="upload-icon">📂</div>
            <div className="upload-title">Drop receipt images / PDFs here</div>
            <div className="upload-sub">Multiple files supported — AI will parse and merge all</div>
            <input
              ref={inputRef}
              type="file" multiple accept="image/*,application/pdf"
              style={{ display: 'none' }}
              onChange={e => addFiles(e.target.files)}
            />
          </div>

          {files.length > 0 && (
            <div className="file-chips">
              {files.map((f, i) => (
                <div className="file-chip" key={i}>
                  📄 {f.name}
                  <button className="file-chip-rm" onClick={e => { e.stopPropagation(); setFiles(p => p.filter((_, j) => j !== i)); }}>×</button>
                </div>
              ))}
            </div>
          )}

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Invoice Date (leave as today for current day; change for backdated receipts)</label>
            <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
            {date !== todayStr() && (
              <div className="alert alert-warning" style={{ marginTop: 8, fontSize: 12 }}>
                ⚠ Backdated entry: this will recalculate totals for <strong>{date}</strong> and update the Drive invoice for that date.
              </div>
            )}
          </div>

          {progress && <div className="alert alert-info">{progress}</div>}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!files.length || uploading}>
            {uploading ? <><Spinner /> Processing...</> : `Parse & Save ${files.length} File${files.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
